/* eslint-disable max-lines-per-function */
import './PROD.scss';
import {PageManager} from '@mvps-genesis/bootstrap';
import ProductImageGallery from 'extensions/product-image-gallery';
import EventBus from '@mvps-genesis/event-bus';
import StorageFactory from '@mvps-genesis/storage-factory';
import 'custom-extensions/searchspring-recommendations';
import 'custom-extensions/bopis/lib/BopisProductElements';

export default class PROD extends PageManager {

	constructor (pageContext) {

		super(pageContext);

		(function (mivaJS) {
			mivaJS.EventBus = EventBus;
		}(window.mivaJS || (window.mivaJS = {})));

	}

	onReady () {

		this.initRecentlyViewed();
		this.initImageGallery();
		this.showBulkOrderModal();
		this.initPurchaseForm();
		this.initStickyHeader();
		this.initScrollToReviews();
		this.initScrollToReviewsForm();
		this.waitlistSubmit();

		/**
		 * This removes the default Miva `closeup_backing` container.
		 */
		var closeupBacking = document.querySelector('div.closeup_backing');

		window.addEventListener('load', function () {
			if (closeupBacking) {
				closeupBacking.parentNode.removeChild(document.querySelector('div.closeup_backing'));
			}
		});

		this.variantCache = {};

		this.mivaEventsListener();
		this.initQuantify();
		this.initShortDesc();
		this.initTabs();
		this.initSubscriptions();
		this.initGModals();
		this.initQuickAdd();
		this.initFindInStores();
		this.initProductReviewsForm();
	}

	initRecentlyViewed () {
		const maxNumber = 5;
		let recentlyViewedProducts = StorageFactory.sessionGet('recentlyViewedProducts') || [];

		if (!recentlyViewedProducts.includes(window.mivaJS.Product_Code)) {

			recentlyViewedProducts.push(window.mivaJS.Product_Code);

			const arrLength = recentlyViewedProducts.length;

			if (arrLength > maxNumber) {
				recentlyViewedProducts.splice(0, arrLength - maxNumber);
			}

			StorageFactory.sessionSet('recentlyViewedProducts', recentlyViewedProducts);
		}

	}

	initImageGallery () {
		const imageSliderElement = document.getElementById('js-product-layout-image-slider');

		if (!imageSliderElement) {
			return;
		}

		/**
		 * Initialize Main-Image, Thumbnails, & PhotoSwipe Close-up Gallery
		 */
		new ProductImageGallery({
			damAssets: this.pageContext.DAM_Assets || [],
			videos: this.pageContext.Product_Videos || []
		});
	}

	showBulkOrderModal () {
		const formElement = document.getElementById('js-purchase-form');
		const bulkOrderLink = document.querySelector('.js-open-bulk-order');

		if (!formElement || !bulkOrderLink) {
			return;
		}

		const quantityElem = formElement.querySelector('input[name="Quantity"]');

		if (!quantityElem) {
			return;
		}

		const quantityChange = () => {
			let quantity = parseInt(quantityElem.value, 10);

			if (quantity > 10) {
				quantityElem.value = 10;
				bulkOrderLink.click();
			}
		};

		quantityElem.addEventListener('change', quantityChange);
		quantityElem.addEventListener('focusout', quantityChange);
	}

	async initPurchaseForm () {
		const purchaseFormElement = document.getElementById('js-purchase-form');

		if (!purchaseFormElement) {
			return;
		}

		// Initialize ajax add to cart functionality
		if (purchaseFormElement.querySelector('.js-add-to-cart')) {
			const {default: AjaxAddToCart} = await import('extensions/ajax-add-to-cart');

			new AjaxAddToCart(purchaseFormElement, {
				scrollToMessage: 'error'
			});
		}

		const {HTMLFormValidator} = await import('extensions/form-validation');

		new HTMLFormValidator({
			fieldContainer: '.g-product-layout-purchase__options-attribute',
			formElement: purchaseFormElement
		});

		this.initWishlists(purchaseFormElement);
	}

	async initWishlists (purchaseFormElement) {
		if (!purchaseFormElement) {
			return;
		}

		const controlElement = document.getElementsByClassName('js-add-to-wishlist-control')[0];

		if (!controlElement) {
			return;
		}

		const dialogElement = document.getElementById(controlElement.getAttribute('aria-controls'));

		if (!dialogElement) {
			return;
		}

		const {WishlistDialog} = await import('extensions/wishlists');
		const wishlistFormElement = document.getElementById('js-create-wishlist-form');

		const wishlistDialog = new WishlistDialog(dialogElement, controlElement, {
			toggleEnabled: Boolean(wishlistFormElement)
		});

		// Wishlist dialog login / registration functionality
		const loginRegisterElement = document.getElementById('js-wishlist-login-register');

		if (loginRegisterElement) {
			const {AjaxLoginRegister} = await import('extensions/ajax-login-register');

			new AjaxLoginRegister(loginRegisterElement, {
				// Add attribute selections or variant_id params to form action if present
				beforeSubmit: (ajaxLoginRegister, event) => {
					const formElement = event.target;
					const formElementActionUrl = new URL(formElement.action);
					const locationParams = new URLSearchParams(window.location.search);
					const variantIdParam = 'variant_id';

					if (locationParams.has(variantIdParam)) {
						formElementActionUrl.searchParams.set(variantIdParam, locationParams.get(variantIdParam));
					} else {
						const purchaseFormData = new FormData(purchaseFormElement);

						if (purchaseFormData.has('Product_Attribute_Count')) {
							const attributeParams = {};

							for (const fieldEntry of purchaseFormData.entries()) {
								if (fieldEntry[0].indexOf('Product_Attributes') !== 0) {
									continue;
								}

								const entryKeyParts = fieldEntry[0].split(':');

								if (entryKeyParts?.length !== 2) {
									continue;
								}

								if (!attributeParams.hasOwnProperty(entryKeyParts[0])) {
									attributeParams[entryKeyParts[0]] = {};
								}

								attributeParams[entryKeyParts[0]][entryKeyParts[1]] = fieldEntry[1];
							}

							for (const param in attributeParams) {
								if (!attributeParams[param].hasOwnProperty('code') || !attributeParams[param].hasOwnProperty('value') || attributeParams[param].value.toString() === '') {
									continue;
								}

								formElementActionUrl.searchParams.set(`${param}:code`, attributeParams[param].code);
								formElementActionUrl.searchParams.set(`${param}:value`, attributeParams[param].value);
							}
						}
					}

					formElement.action = formElementActionUrl.toString();
				}
			});
		}

		// Wishlist dialog add to wishlist and insert wishlist functionality
		if (wishlistFormElement) {
			const {AjaxAddToWishlist, AjaxInsertWishlist} = await import('extensions/wishlists');
			const {MessageContainer, Message} = await import('extensions/messages');

			const messageContainerElement = purchaseFormElement.getElementsByClassName('js-add-to-cart-message')[0];
			let messageContainer = null;

			if (messageContainerElement) {
				messageContainer = new MessageContainer({
					container: messageContainerElement
				});
			}

			new AjaxInsertWishlist(wishlistFormElement);

			const ajaxAddToWishlist = new AjaxAddToWishlist(purchaseFormElement);
			const wishlistCountElements = document.getElementsByClassName('js-wishlists-count');

			/**
			 * Add new wishlist action element to action elements list on successful
			 * creation, and toggle display back to add to wishlist UI. Update elements
			 * containing wishlist counts with new wishlist count.
			 */
			EventBus.addEventListener('MVPS:insertWishlist', (event) => {
				const wishlist = event.detail?.data?.wishlist;

				if (!wishlist) {
					return;
				}

				const actionElement = ajaxAddToWishlist.createActionElement(wishlist);

				ajaxAddToWishlist.appendActionElement(actionElement);

				const autohideTime = ajaxAddToWishlist.messageOptions.success.options.autohideTime ?? 5000;

				ajaxAddToWishlist.createMessage(event.detail.message, {
					...ajaxAddToWishlist.messageOptions.success.options,
					autohide: true,
					autohideTime: autohideTime
				});

				setTimeout(() => {
					ajaxAddToWishlist.messageContainer.clear();
				}, autohideTime);

				wishlistDialog.toggle.elements.primary.controlElement.click();

				actionElement.focus();

				if (wishlistCountElements) {
					let wishlistsCount = parseInt(wishlistCountElements[0].textContent, 10);

					if (!isNaN(wishlistsCount)) {
						wishlistsCount += 1;

						for (const wishlistCountElement of wishlistCountElements) {
							wishlistCountElement.textContent = wishlistsCount;
						}
					}
				}
			});

			/**
			 * Hide wishlist dialog on success and display success message in
			 * purchase form message element
			 */
			EventBus.addEventListener('MVPS:addToWishlist', (event) => {
				const addToWishlistSuccess = event.detail?.success;

				if (!addToWishlistSuccess || !messageContainer) {
					return;
				}

				messageContainer.add(new Message(event.detail.message, {
					...ajaxAddToWishlist.messageOptions.success.options,
					autohide: true
				}));

				wishlistDialog.controlElement.click();
				ajaxAddToWishlist.messageContainer.clear();
			});
		}
	}

	async initStickyHeader () {
		const stickyHeaderElement = document.getElementById('js-sticky-header');
		const atcElement = document.getElementsByClassName('js-add-to-cart')[0];
		const isInstoreOnly = window.mivaJS.Is_Instore_Only;

		if (!stickyHeaderElement || !atcElement || !('IntersectionObserver' in window) || isInstoreOnly) {
			return;
		}

		const {default: ProductStickyHeader} = await import('extensions/product-sticky-header');

		new ProductStickyHeader(stickyHeaderElement, {
			targetAddToCartElement: atcElement
		});
	}

	mivaEventsListener () {
		const observer = new MutationObserver((_mutations, mo) => {
			if (typeof window.MivaEvents !== 'undefined') {
				mo.disconnect();

				// eslint-disable-next-line new-cap
				window.MivaEvents.SubscribeToEvent('variant_changed', (productData) => this.variantChanged(productData));
			}
		});

		observer.observe(document, {
			childList: true,
			subtree: true
		});
	}

	variantChanged (productData) {
		this.updateVariantURL(productData.variant_id);
		this.updateVariantData(productData);
	}

	updateVariantURL (variantId) {

		let newSearch = '';

		if ((/variant_id=\d+/).test(window.location.search)) {
			newSearch = window.location.search.replace(/variant_id=\d+/, 'variant_id=' + variantId);
		} else if (window.location.search) {
			newSearch = window.location.search + '&variant_id=' + variantId;
		} else {
			newSearch = '?variant_id=' + variantId;
		}

		window.history.replaceState(null, '', window.location.pathname + newSearch + window.location.hash);

	}

	async updateVariantData (productData) {
		await this.getvariantData(productData);

		this.updateVolumePricing(this.variantCache[productData.variant_id]);
		this.updateShippingMessage(this.variantCache[productData.variant_id]);
		this.updateProductFlags(this.variantCache[productData.variant_id]);
		this.updateClearanceLabel(this.variantCache[productData.variant_id]);
		this.updateAdditionalPrice(this.variantCache[productData.variant_id]);
		this.updateProductOriginalPrice(this.variantCache[productData.variant_id]);
		this.updateGTMDataAttributes(this.variantCache[productData.variant_id]);
		this.addListrakProductBrowse(this.variantCache[productData.variant_id]);
		this.updateVariantIsPurchasable(this.variantCache[productData.variant_id]);

		// Dispatch event -- used for BOPIS
		EventBus.dispatchEvent('MVPS:productVariantChanged', {
			productData: this.variantCache[productData.variant_id]
		});

		// Rebind event
		this.initFindInStores();
	}

	async getvariantData (productData) {
		if (this.variantCache?.[productData.variant_id]) {
			return this.variantCache[productData.variant_id];
		}

		/* eslint-disable camelcase */
		const response = await fetch(this.pageContext.Japi_Url, {
			body: JSON.stringify({
				function: 'load-variant-data',
				product_code: productData.product_code,
				variant_id: productData.variant_id
			}),
			headers: {
				'Content-Type': 'application/json'
			},
			method: 'POST'
		});
		/* eslint-enable camelcase */

		if (!response.ok) {
			throw Error(response.statusText);
		}

		const json = await response.json();

		this.variantCache[productData.variant_id] = {
			...json,
			variant: {
				...json.variant,
				basePrice: productData.data.base_price,
				price: productData.data.price,
				retail: productData.data.retail
			}
		};

		return true;
	}

	updateVolumePricing (data) {
		let html = '';

		if (data && data.volume_pricing && data.volume_pricing.hasOwnProperty('template')) {
			html = data.volume_pricing.template;
		}

		const volumePricingElements = document.getElementsByClassName('js-volume-pricing');

		for (const volumePricingElement of volumePricingElements) {
			volumePricingElement.innerHTML = html;
		}
	}

	updateClearanceLabel (data) {
		const clearanceLabelElement = document.getElementsByClassName('g-product-layout-details__pricing-original-label');

		for (const label of clearanceLabelElement) {
			if (data.variant.basePrice >= data.variant.customfield_values.original_price) {
				label.classList.add('u-hidden');
			} else {
				label.classList.remove('u-hidden');

				if (data.variant.basePrice > data.variant.price) {
					label.classList.add('has-sale');
				} else {
					label.classList.remove('has-sale');
				}
			}
		}
	}

	updateAdditionalPrice (data) {
		const additionalPriceElement = document.getElementsByClassName('g-product-layout-details__pricing-original');

		for (const price of additionalPriceElement) {
			if (data.variant.basePrice > data.variant.price) {
				price.classList.add('has-sale');
			} else {
				price.classList.remove('has-sale');
			}
		}
	}

	updateProductOriginalPrice (data) {
		const productOriginalPriceElements = document.getElementsByClassName('js-product-original-price');

		// eslint-disable-next-line no-nested-ternary
		const productOriginalPriceData = data?.variant?.templates?.original_price ? data.variant.templates.original_price : data?.product?.templates?.original_price ? data.product.templates.original_price : '';

		for (const originalPrice of productOriginalPriceElements) {
			originalPrice.innerHTML = productOriginalPriceData;

			if (data.variant.basePrice >= data.variant.customfield_values.original_price) {
				originalPrice.parentNode.style.display = 'none';
				document.getElementById('price-value').classList.remove('has-clearance');
			} else {
				originalPrice.parentNode.style.display = 'block';
				document.getElementById('price-value').classList.add('has-clearance');
			}
		}
	}

	updateProductFlags (data) {
		const productFlagElements = document.getElementsByClassName('js-product-flag');
		const productFlagData = data?.variant?.templates?.flag ? data.variant.templates.flag : '';

		for (const flag of productFlagElements) {
			flag.innerHTML = productFlagData;
		}
	}

	updateShippingMessage (data) {
		const shippingAvailabilityMessage = data?.variant?.customfield_values?.shipping_availability ? data.variant.customfield_values.shipping_availability : '';
		const anticipatedShipDate = data?.variant?.customfield_values?.anticipated_ship_date ? data.variant.customfield_values.anticipated_ship_date : '';
		const shippingMessageContainer = document.querySelector('.js-product-shipping-message');
		let shippingMessages = '';

		if (shippingMessageContainer) {
			shippingMessageContainer.innerHTML = '';
		}

		if (shippingAvailabilityMessage.length) {
			shippingMessages += `<p>${shippingAvailabilityMessage}</p>`;
		}

		if (anticipatedShipDate.length) {
			shippingMessages += `<p><strong>This product is a Special Event item, additional items part of the same order will not ship until the Special Event item is available.</strong></p>`;
		}

		if (shippingMessages.length === 0) {
			return;
		}

		if (shippingMessageContainer) {
			shippingMessageContainer.innerHTML = `<div>${shippingMessages}</div>`;
		}
	}

	updateShippingFlag (data) {
		const shippingFlagElements = document.getElementsByClassName('js-product-shipping-flag');
		const shippingFlagData = data?.variant?.templates?.shipping_flag ? data.variant.templates.shipping_flag : '';

		for (const flag of shippingFlagElements) {
			flag.innerHTML = shippingFlagData;
		}
	}

	updateGTMDataAttributes (data) {
		if (!data || !data.success || !data.variant || !data.variant.id) {
			return;
		}

		const productFormElement = document.getElementById('js-purchase-form');

		const variantSKU = data.variant.sku.split(':')[1];

		productFormElement.dataset.gtmVariant = variantSKU ? variantSKU.trim() : data.variant.code;
	}

	/**
	 * This function is meant to toggle purchase form elements visibility state.
	 * This was meant to be used to toggle the in-store pick up status where the purchase form cta and attribute options would be hidden
	 * @param {HTMLCollection} elements
	 * @param {Boolean} isHidden
	 */
	togglePurchaseFormElements (elements, isHidden) {
		if (!elements) {
			return;
		}

		for (const element of elements) {
			if (isHidden) {
				element.setAttribute('aria-hidden', 'true');
			} else {
				element.setAttribute('aria-hidden', 'false');
			}

			element.classList.toggle('u-hidden', isHidden);
		}
	}

	async initQuantify () {
		const {Quantify} = await import('extensions/quantify');

		new Quantify();
	}

	async initShortDesc () {
		const shortDescElement = document.getElementsByClassName('g-short-desc')[0];

		if (!shortDescElement) {
			return;
		}

		let maxWordCount = parseInt(shortDescElement.getAttribute('data-max-word-count'), 10);

		if (isNaN(maxWordCount)) {
			maxWordCount = 20;
		}

		const {default: ShortDesc} = await import('extensions/short-description');

		new ShortDesc({
			maxWordCount: maxWordCount,
			scrollToElement: false,
			shortDescElement: shortDescElement
		});
	}

	async initTabs () {
		const productTabs = document.getElementsByClassName('g-tabcordion');
		const productTabsLength = productTabs.length;

		if (!productTabsLength) {
			return;
		}

		const {Tabcordion} = await import('extensions/tabs');

		for (let i = 0; i < productTabsLength; i++) {
			productTabs[i].tabcordion = new Tabcordion(productTabs[i], {
				accordionMode: 768
			});

			if (i === 0) {
				const shortDescLinkElement = document.querySelector('.g-short-desc__link');

				if (shortDescLinkElement) {
					shortDescLinkElement.addEventListener('click', (event) => {
						const hashElements = productTabs[i].tabcordion.getElementsByHash(shortDescLinkElement.hash);

						if (hashElements) {
							event.preventDefault();

							productTabs[i].tabcordion.showPanel(hashElements, {
								scrollIntoView: productTabs[i].tabcordion.hashOptions.scrollIntoView
							});
						}
					});
				}
			}
		}
	}

	initSubscriptions () {
		const subTermElement = document.getElementById('js-subscription-terms');
		const subChoiceElements = document.getElementsByClassName('js-subscription-choice');

		if (!subTermElement || !subChoiceElements.length) {
			return;
		}

		const subtermOptionsElement = subTermElement.querySelector('[name="Product_Subscription_Term_ID"]');

		if (!subtermOptionsElement) {
			return;
		}

		const subTermControlElementId = 'js-subscription-term-control';

		for (const element of subChoiceElements) {
			element.addEventListener('click', () => {
				if (element.id === subTermControlElementId && element.checked) {
					subTermElement.removeAttribute('hidden');
					subtermOptionsElement.disabled = false;
				} else {
					subTermElement.setAttribute('hidden', '');
					subtermOptionsElement.disabled = true;
				}
			});
		}
	}

	async initGModals () {
		const elements = document.querySelectorAll('g-modal');

		if (!elements.length > 0) {
			return;
		}

		await import('custom-extensions/g-modal');
	}

	async initQuickAdd () {
		if (!window.mivaJS?.Is_Quick_Add) {
			return;
		}

		const {default: QuickAdd} = await import('custom-extensions/quick-add');
		new QuickAdd();
	}

	initFindInStores () {
		const triggers = document.getElementsByClassName('js-bopis-findinstore-trigger__element');

		if (triggers.length === 0) {
			return;
		}

		const productModal = document.getElementById('js-bopis-product');

		if (!productModal) {
			return;
		}

		productModal.addEventListener('close', () => this.findInStoresModalOpen(triggers, false));
		productModal.addEventListener('open', () => this.findInStoresModalOpen(triggers, true));

		const eventAddedClass = 'js-bopis-event-added';

		for (const trigger of triggers) {
			if (trigger.classList.contains(eventAddedClass)) {
				continue;
			}

			trigger.classList.add(eventAddedClass);

			trigger.addEventListener('click', (e) => {
				e.preventDefault();
				productModal.open();
			});
		}
	}

	findInStoresModalOpen (triggers, open = true) {
		for (const trigger of triggers) {
			trigger.ariaExpanded = open;
		}
	}

	async initScrollToReviews () {
		const triggerElements = document.getElementsByClassName('js-scroll-to-reviews');

		if (!triggerElements.length) {
			return;
		}

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest'
			}
		});
	}

	async initScrollToReviewsForm () {
		const triggerElements = document.getElementsByClassName('js-scroll-to-reviews-form');

		if (!triggerElements.length) {
			return;
		}

		const {default: ScrollTo} = await import('extensions/scroll-to');

		new ScrollTo(triggerElements, {
			scrollIntoViewOptions: {
				behavior: 'smooth',
				block: 'start',
				inline: 'nearest'
			}
		});
	}

	async initProductReviewsForm () {
		const reviewsFormElement = document.getElementById('js-product-reviews-form');

		if (!reviewsFormElement) {
			return;
		}

		const {FormSubmitEvents} = await import('extensions/checkout-pages');

		new FormSubmitEvents({
			formElement: reviewsFormElement
		});
	}

	addListrakProductBrowse (data) {
		if (!data?.variant?.sku) {
			return;
		}

		const ltkObserver = new MutationObserver((_mutations, mo) => {
			if (typeof window._ltk === 'object') {
				mo.disconnect();

				// eslint-disable-next-line new-cap
				let sku = data.variant.sku;
				const splitSkus = sku.split(' : ');
				if (splitSkus[1]) {
					sku = splitSkus[1];
				}
				window._ltk.Activity.AddProductBrowse(sku);
			}
		});

		ltkObserver.observe(document, {
			childList: true,
			subtree: true
		});
	}

	async updateVariantIsPurchasable (data) {
		if (!data || !data.success || !data.variant || !data.variant.id) {
			return;
		}

		const purchaseFormElement = document.getElementById('js-purchase-form');
		const messageContainerElement = purchaseFormElement.getElementsByClassName('js-add-to-cart-message')[0];
		const purchaseButton = purchaseFormElement.querySelector('.js-add-to-cart');
		const wishlistButton = purchaseFormElement.querySelector('.js-add-to-wishlist-control');

		// Reset messaging
		messageContainerElement.innerHTML = '';

		if (data.variant.customfield_values.drop_ship_item === '' && data.variant.customfield_values.faceted_content === 1) {
			const {MessageContainer, Message} = await import('extensions/messages');
			let messageContainer = null;

			purchaseButton.disabled = true;
			wishlistButton.disabled = true;

			if (messageContainerElement) {
				messageContainer = new MessageContainer({
					container: messageContainerElement
				});

				messageContainer.add(new Message('The selected size is currently unavailable for online purchase. Please check back later.', {
					autohide: false,
					type: 'error'
				}));
			}
		} else {
			purchaseButton.disabled = false;
			wishlistButton.disabled = false;
		}
	}

	waitlistSubmit () {
		const waitlistModalElement = document.getElementById('js-waitlist');
		const waitlistFormElement = document.getElementById('js-waitlist-form');

		if (!waitlistFormElement) {
			return;
		}

		const waitlistFormListElement = waitlistFormElement.querySelector('.g-product-waitlist__form-list');

		waitlistFormElement.addEventListener('submit', async (event) => {
			const messageElement = document.getElementById('js-waitlist-message');

			if (!messageElement) {
				return;
			}

			const messageIconElement = messageElement.querySelector('.g-product-waitlist__message-icon');
			const messageTextElement = messageElement.querySelector('.g-product-waitlist__message-text');

			event.preventDefault();

			const requestUrl = waitlistFormElement.action ?? this.pageContext.Json_Url;
			const formData = new FormData(waitlistFormElement);

			const response = await fetch(requestUrl, {
				body: formData,
				method: waitlistFormElement.method ?? 'POST'
			});

			if (!response.ok) {
				return;
			}

			const json = await response.json();

			if (!json) {
				return;
			}

			if (json.success) {
				waitlistFormListElement.classList.add('u-hidden');

				messageElement.classList.remove('u-hidden');
				messageElement.classList.add('g-messages--success');

				messageIconElement?.classList.remove('u-icon-error');
				messageIconElement?.classList.add('u-icon-check');

				if (messageTextElement) {
					messageTextElement.innerHTML = 'We\'ll Notify You When It\'s Back';
				}

				setTimeout(() => {
					document.body.style = {};

					waitlistModalElement.classList.remove('is-open');
					waitlistModalElement.setAttribute('aria-hidden', 'true');

					waitlistFormElement.reset();

					waitlistFormListElement.classList.remove('u-hidden');

					messageElement.classList.add('u-hidden');
					messageElement.classList.remove('g-messages--success');
				}, 3000);
			} else if (json.error_code === 'MOD-SYS-TGW-0036' || json.error_code === 'MOD-SYS-TGW-0037' || json.error_code === 'MOD-SYS-TGW-0050' || json.error_code === 'MOD-SYS-TGW-0051') {
				waitlistFormListElement.classList.add('u-hidden');

				messageElement.classList.remove('u-hidden');
				messageElement.classList.add('g-messages--success');

				messageIconElement?.classList.remove('u-icon-error');
				messageIconElement?.classList.add('u-icon-check');

				if (messageTextElement) {
					messageTextElement.innerHTML = json.error_message;
				}

				setTimeout(() => {
					waitlistFormElement.reset();

					waitlistFormListElement.classList.remove('u-hidden');

					messageElement.classList.add('u-hidden');
					messageElement.classList.remove('g-messages--success');
				}, 5000);
			} else {
				messageElement.classList.remove('u-hidden');
				messageElement.classList.add('g-messages--error');

				messageIconElement?.classList.remove('u-icon-check');
				messageIconElement?.classList.add('u-icon-error');

				if (messageTextElement) {
					messageTextElement.innerHTML = json.error_message;
				}

				setTimeout(() => {
					messageElement.classList.add('u-hidden');
					messageElement.classList.remove('g-messages--error');
				}, 5000);
			}
		});
	}
}
